import { createClient, type SupabaseClient } from '@supabase/supabase-js'

/**
 * The browser's Supabase client.
 *
 * ── ONLY THE PUBLISHABLE KEY EVER GETS HERE ────────────────────────────────
 * This file is bundled and served to every visitor, so it may only ever hold
 * the publishable key. That key is safe in public precisely because the
 * database refuses to act on it beyond what row level security allows: a
 * customer can read their own orders and nothing else. Every write — prices,
 * statuses, registrations — goes through the Worker, which holds the secret
 * key server-side.
 *
 * If you ever find yourself wanting `SUPABASE_SECRET_KEY` in this file, the
 * thing you actually want is a new Worker endpoint.
 *
 * ── CREATED LAZILY, BECAUSE GATSBY RENDERS THIS ON A SERVER ────────────────
 * `createClient` reaches for `window.localStorage` to persist a session, which
 * does not exist during the static build. The client is therefore built on
 * first use in the browser, and `null` on the server — callers handle that.
 */

const URL = process.env.GATSBY_SUPABASE_URL ?? ''
const PUBLISHABLE_KEY = process.env.GATSBY_SUPABASE_PUBLISHABLE_KEY ?? ''

/** False when the build has no Supabase configured, so pages can say so. */
export const supabaseConfigured = URL !== '' && PUBLISHABLE_KEY !== ''

let client: SupabaseClient | null = null

export function getSupabase(): SupabaseClient | null {
  if (typeof window === 'undefined' || !supabaseConfigured) return null
  if (!client) {
    client = createClient(URL, PUBLISHABLE_KEY, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        // The sign-in link Supabase emails lands back on the site with tokens
        // in the URL fragment; this is what exchanges them for a session.
        detectSessionInUrl: true,
      },
    })
  }
  return client
}
